import { Card } from '@/components/common/card'
import { Loader } from '@/components/common/loader'
import { ScrollArea, ScrollBar } from '@/components/common/scrollArea'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/common/table'
import { LoadingIcon } from '@/components/icons/loading'
import { useBorrowMarketEventsQuery } from '@/queries/borrowMarketEvents.query'
import {
  type BorrowMarketAssetPositionBalance,
  useBorrowMarketPositionQuery,
} from '@/queries/borrowMarketPosition.query'
import type { BorrowMarketEvent } from '@/utils/internalAppSdk'
import type { BorrowMarketAssetData, BorrowMarketDetail } from '@/utils/internalAppSdk'
import { SECOND_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import {
  formatAmount,
  formatDate,
  formatDecimalToPercentage,
  formatPreciseTokenValue,
  formatUSD,
} from '@vaultsfyi/common/utils'
import type { ReactNode } from 'react'
import type { Address } from 'viem'
import { formatUnits } from 'viem'

const FORMAT_CONFIG = { maximumFractionDigits: 4 } as const
const COLUMNS = ['Date & Time', 'Activity', 'Amount', 'Asset']
const LIQUIDATION_RISK_HEALTH_FACTOR = 1

interface BorrowMarketPositionDetailsProps {
  address: Address
  market: BorrowMarketDetail
  borrowAssetData: BorrowMarketAssetData
}

export const BorrowMarketPositionDetails = ({ address, market, borrowAssetData }: BorrowMarketPositionDetailsProps) => {
  const {
    data: position,
    isError: isPositionError,
    isFetching: isPositionFetching,
  } = useBorrowMarketPositionQuery({
    address,
    network: market.network.name,
    marketId: market.marketId,
    assetId: borrowAssetData.marketAssetId,
  })
  const {
    data: events,
    isError: isEventsError,
    isFetching: isEventsFetching,
  } = useBorrowMarketEventsQuery({
    address,
    network: market.network.name,
    marketId: market.marketId,
    assetId: borrowAssetData.marketAssetId,
  })

  if (isPositionFetching && !position) {
    return (
      <div className="flex w-full flex-col items-center justify-center gap-4 py-12">
        <LoadingIcon size={48} className="animate-spin" />
        <p className="font-bold text-xl">Loading position details...</p>
      </div>
    )
  }

  if (isPositionError) {
    return (
      <div className="flex w-full flex-col items-center justify-center gap-4 py-12 text-center">
        <p className="font-medium text-heading-foreground text-lg">Failed to load position details</p>
        <p className="text-body-foreground text-sm">Try refreshing the page or reconnecting your wallet.</p>
      </div>
    )
  }

  if (!position) {
    return null
  }

  return (
    <div className="flex flex-col gap-6">
      <BorrowMarketPositionTiles position={position} borrowAssetData={borrowAssetData} />
      <BorrowMarketPositionActivity
        events={events}
        borrowAssetData={borrowAssetData}
        isError={isEventsError}
        isFetching={isEventsFetching}
      />
    </div>
  )
}

const BorrowMarketPositionTiles = ({
  position,
  borrowAssetData,
}: {
  position: BorrowMarketAssetPositionBalance
  borrowAssetData: BorrowMarketAssetData
}) => (
  <div className="grid gap-3 md:grid-cols-3">
    <PositionTile label="Current borrow APY" value={formatOptionalPercentage(borrowAssetData.borrowRate)} />
    <PositionTile label="Position size" value={getPositionSize(position)} secondary={formatUSD(position.borrowedUsd)} />
    <PositionTile
      label="Health factor"
      value={formatHealthFactor(position.healthFactor)}
      valueClassName={getRiskColor(position.healthFactor)}
    />
  </div>
)

const BorrowMarketPositionActivity = ({
  events,
  borrowAssetData,
  isError,
  isFetching,
}: {
  events: BorrowMarketEvent[]
  borrowAssetData: BorrowMarketAssetData
  isError: boolean
  isFetching: boolean
}) => (
  <div className="flex flex-col gap-4">
    <h4 className="font-medium text-heading-foreground text-lg">Activity</h4>
    <ScrollArea className="w-full overflow-hidden rounded-2xl border">
      <Table className="min-w-96">
        <TableHeader>
          <TableRow variant="header" className="border-b">
            {COLUMNS.map((column) => (
              <TableHead key={column} className="h-10 whitespace-nowrap px-4 text-xs uppercase last:text-right">
                {column}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isError ? (
            <BorrowMarketPositionActivityState>Failed to load borrow market activity</BorrowMarketPositionActivityState>
          ) : isFetching && events.length === 0 ? (
            <BorrowMarketPositionActivityState>
              <Loader />
            </BorrowMarketPositionActivityState>
          ) : events.length === 0 ? (
            <BorrowMarketPositionActivityState>
              This wallet has no actions on this borrow market
            </BorrowMarketPositionActivityState>
          ) : (
            events.map((event) => (
              <TableRow key={`${event.transactionHash}-${event.logIndex}`}>
                <TableCell className="whitespace-nowrap px-4 py-5 tabular-nums">
                  {formatDate({
                    timestamp: event.timestamp * SECOND_IN_MILLISECONDS,
                    showYear: true,
                    showTime: true,
                  })}
                </TableCell>
                <TableCell className={`px-4 py-5 ${getEventAmountColor(event.eventType)}`}>
                  {getEventLabel(event.eventType)}
                </TableCell>
                <TableCell className={`px-4 py-5 tabular-nums ${getEventAmountColor(event.eventType)}`}>
                  {getEventAmountPrefix(event.eventType)}
                  {Intl.NumberFormat('en-US', FORMAT_CONFIG).format(
                    Number(formatUnits(BigInt(event.assetAmountNative), borrowAssetData.asset.decimals))
                  )}
                </TableCell>
                <TableCell className="px-4 py-5 text-right">{borrowAssetData.asset.symbol}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  </div>
)

interface PositionTileProps {
  label: string
  value: string
  secondary?: string
  valueClassName?: string
}

const PositionTile = ({ label, value, secondary, valueClassName }: PositionTileProps) => (
  <Card className="flex min-h-[100px] flex-col gap-2 p-4">
    <p className="text-body-foreground text-sm">{label}</p>
    <div className="flex min-w-0 flex-col gap-0.5">
      <p className={`truncate font-medium text-2xl text-heading-foreground tabular-nums ${valueClassName ?? ''}`}>
        {value}
      </p>
      {secondary && <p className="truncate text-body-foreground text-sm tabular-nums">{secondary}</p>}
    </div>
  </Card>
)

const BorrowMarketPositionActivityState = ({ children }: { children: ReactNode }) => (
  <TableRow>
    <TableCell colSpan={COLUMNS.length} className="text-center">
      <div className="flex w-full flex-col items-center justify-center gap-4 py-4">{children}</div>
    </TableCell>
  </TableRow>
)

const formatOptionalPercentage = (value: number | undefined) => {
  return value === undefined ? '-' : formatDecimalToPercentage(value)
}

const formatHealthFactor = (healthFactor: number | undefined) => {
  return healthFactor === undefined
    ? '-'
    : formatAmount(healthFactor, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const getRiskColor = (healthFactor: number | undefined) => {
  if (healthFactor === undefined) return 'text-heading-foreground'
  if (healthFactor <= LIQUIDATION_RISK_HEALTH_FACTOR) return 'text-destructive'
  if (healthFactor < 1.5) return 'text-warning'
  return 'text-success'
}

const getPositionSize = (position: BorrowMarketAssetPositionBalance) => {
  const { asset } = position.asset
  return `${formatPreciseTokenValue({
    value: position.asset.borrowedNative,
    decimals: asset.decimals,
  })} ${asset.symbol}`
}

const getEventLabel = (eventType: BorrowMarketEvent['eventType']) => {
  switch (eventType) {
    case 'supply':
      return 'Supply'
    case 'withdraw':
      return 'Withdraw'
    case 'borrow':
      return 'Borrow'
    case 'repay':
      return 'Repay'
  }
}

const getEventAmountPrefix = (eventType: BorrowMarketEvent['eventType']) => {
  return eventType === 'withdraw' || eventType === 'repay' ? '-' : '+'
}

const getEventAmountColor = (eventType: BorrowMarketEvent['eventType']) => {
  return eventType === 'withdraw' || eventType === 'repay' ? 'text-destructive' : 'text-success'
}
